import React from "react"

/**
 * Hook that provides a restartable waiting mechanism with maximum timeout functionality.
 * Creates a promise that can be resolved either by timeout or manual cancellation,
 * with the ability to restart the timeout when new events occur.
 *
 * @param restartTimeout - Time to wait for next event before resolving (default: 500ms)
 * @param maxTimeout - Maximum time to wait before auto-resolving (default: 2500ms)
 * @returns An object containing:
 *   - startWait: Function to begin waiting with timeout mechanism
 *   - restartWait: Function to restart the timeout when a new event occurs
 *   - cancelWait: Function to cancel waiting and clear all timeouts
 *   - isWaiting: Function to check if currently waiting
 */
export const useRestartableWait = (restartTimeout: number, maxTimeout: number) => {
  const resolve = React.useRef<(() => void) | null>(null)
  const restartTimeoutRef = React.useRef<number | null>(null)
  const maxTimeoutRef = React.useRef<number | null>(null)

  const cancelWait = React.useCallback(() => {
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current)
    }
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current)
    }

    // Resolve the promise if it exists
    if (resolve.current) {
      resolve.current()
      resolve.current = null
    }
  }, [])

  // Restart the wait by starting a new timeout.
  // This should be called when a new event occurs.
  const restartWait = React.useCallback(() => {
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current)
    }

    restartTimeoutRef.current = setTimeout(() => {
      cancelWait()
    }, restartTimeout)
  }, [cancelWait, restartTimeout])

  // Begin waiting and create a promise that will be resolved
  // when either the max timeout is reached or the restartable timeout expires.
  // The restartable timeout can be restarted by calling restartWait().
  const startWait = React.useCallback(async () => {
    const promise = new Promise<void>((_resolve) => {
      resolve.current = _resolve
    })

    // Maximum timeout
    maxTimeoutRef.current = setTimeout(() => {
      cancelWait()
    }, maxTimeout)

    // Start the next timeout
    restartWait()

    return promise
  }, [cancelWait, restartWait, maxTimeout])

  // Check if currently waiting
  const isWaiting = React.useCallback(() => {
    return resolve.current !== null
  }, [])

  return {
    startWait,
    restartWait,
    cancelWait,
    isWaiting,
  }
}
